import { useEffect, useState } from 'react';
import { Marquee } from './canvas/Marquee';
import { CanvasSurface } from './render/CanvasSurface';
import { CmdK } from './chrome/CmdK';
import { ActionChip } from './chrome/ActionChip';
import { SizeInspector } from './chrome/SizeInspector';
import { hasReplayed, runFirstRunReplay } from './templates/firstRunReplay';
import { ProfileMenu } from './chrome/ProfileMenu';
import { ProjectNameChip } from './chrome/ProjectNameChip';
import { SettingsModal } from './chrome/SettingsModal';
import { PresenceProvider } from './presence/PresenceProvider';
import { PresenceCursors } from './presence/PresenceCursors';
import { RemoteSelection } from './presence/RemoteSelection';
import { Facepile } from './presence/Facepile';
import { ConnectionStatus, SyncUpgradeBanner } from './presence/ConnectionStatus';
import { currentReadOnly } from './chrome/readonly';
import { spawnTsxComponent } from './chrome/spawnTsx';
import { ensurePanelChat } from './chrome/panel/panelFrames';
import { newConversation } from './agent/chatTurn';
import { ContextMenu } from './chrome/ContextMenu';
import { TourPlayer } from './chrome/TourPlayer';
import { useCanvasStore } from './store/canvas-store';
import { boundsOf } from './store/selectors';
import { isPanelOwned } from './store/types';
import { EditorPanel } from './chrome/panel/EditorPanel';
import { usePanelStore } from './chrome/panel/panel-store';
import { handlePaste, recordCursor, pasteAsEmbed, handleDragOver, handleDrop } from './chrome/paste';
import { CommandPalette } from './chrome/CommandPalette';
import { useAppCommands } from './chrome/app-commands';
import { deleteSelection, duplicateSelection, selectAll } from './chrome/selectionActions';
import { useToolStore } from './chrome/tool-store';
import { BookmarkletModal } from './chrome/BookmarkletModal';
import { ProjectsScreen } from './projects/ProjectsScreen';
import { useProjectsScreen } from './projects/projectsScreen-store';
import { openProjectById } from './projects/openProject';
import { useCurrentProject } from './projects/currentProject';
import { syncController } from './projects/useProjects';
import { isLiveSyncEnabled } from './projects/liveSync';
import { isApplyingDoc } from './persistence/local-store';
import { encodeDoc } from './io/serialize';
import { ConnectAgent } from './chrome/ConnectAgent';
import { LayerOverlay } from './chrome/LayerOverlay';
import { LayerDropIndicator } from './chrome/LayerDropIndicator';
import { FlashOverlay } from './chrome/FlashOverlay';
import { ancestorChain, parentCrumb, firstChildLayer, siblingLayer } from './chrome/layerSelect';
import { getRenderHtml, framesById } from './store/commands';
import { CommentPopover } from './chrome/comments/CommentPopover';
import { MediaSwapModal } from './chrome/mediaSwap/MediaSwapModal';
import { StockPhotoModal } from './chrome/stockPhotos/StockPhotoModal';
import { CheckpointsModal } from './chrome/history/CheckpointsModal';
import { PublishModal } from './chrome/publish/PublishModal';
import { collectAssetIds } from './assets/resolveAssets';
import { ensureAssetsLoaded } from './assets/assetUrls';
import { useTailwindCompile } from './style/useTailwindCompile';
import { useFrameVersioning } from './history/useFrameVersioning';
import { useThemeFonts } from './style/themeFonts';
import { useGlobalZoomGuard } from './canvas/useGlobalZoomGuard';
import { TimelinePanel } from './chrome/timeline/TimelinePanel';
import { useTimelineStore } from './chrome/timeline/timeline-store';
import { installErrorCapture } from './agent/errorBuffer';

installErrorCapture();

export function App({ routeProjectId }: { routeProjectId?: string } = {}) {
  const doc = useCanvasStore((s) => s.doc);
  const [bookmarkletOpen, setBookmarkletOpen] = useState(false);
  const currentProjectId = useCurrentProject((s) => s.projectId);
  const commands = useAppCommands(() => setBookmarkletOpen(true));

  // Runtime Tailwind: compile frame-authored utility classes + themes into one
  // global sheet. Serves both render paths (canvas snapshots the styled DOM).
  useTailwindCompile();
  // Debounced per-frame content versioning into IndexedDB.
  useFrameVersioning();
  // Load any Google Fonts referenced by theme typography (app-controlled link).
  useThemeFonts();
  // Block browser pinch page-zoom app-wide; route pinch to canvas zoom instead.
  useGlobalZoomGuard();

  // Preload IndexedDB-stored assets referenced by the doc → objectURLs, so
  // asset:// refs resolve even for frames culled offscreen at first paint.
  useEffect(() => {
    const ids = collectAssetIds(doc);
    if (ids.length) void ensureAssetsLoaded(ids);
  }, [doc]);

  // Clipboard paste → new frame. Wired at app level (above the CanvasSurface).
  useEffect(() => {
    const onMove = (e: MouseEvent) => recordCursor(e.clientX, e.clientY);
    // ⌘⇧V / Ctrl+Shift+V → paste clipboard as a sandboxed live embed (runs JS).
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const ls = useCanvasStore.getState().layerSelection;
        if (ls) {
          // Step out one breadcrumb level: layer → enclosing layer → frame.
          const st = useCanvasStore.getState();
          const frame = st.doc.frames.find((f) => f.id === ls.frameId);
          const crumbs = frame ? ancestorChain(getRenderHtml(frame, framesById(st.doc.frames)), ls.cfNodeId) : [];
          const parent = parentCrumb(crumbs, ls.cfNodeId);
          st.setLayerSelection(parent ? { frameId: ls.frameId, cfNodeId: parent } : null);
          return;
        }
      }
      // Arrow keys walk the layer tree while a layer is selected: Up = out to the
      // enclosing layer, Down = in to the first child, Left/Right = prev/next sibling.
      if (
        (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') &&
        !isEditableTarget(e.target)
      ) {
        const st = useCanvasStore.getState();
        const ls = st.layerSelection;
        if (!ls) {
          // No layer selected → keyboard-nudge the selected frame(s) (styleguide
          // §21, re-assessment B6). Arrow = ±1, Shift = ±10; Alt+Arrow resizes.
          const sel = st.selection;
          if (sel.length === 0) return;
          e.preventDefault();
          const step = e.shiftKey ? 10 : 1;
          const d = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] }[e.key]!;
          if (e.altKey) {
            const f = st.doc.frames.find((x) => x.id === sel[0]);
            if (!f) return;
            st.dispatch({ t: 'frame.resize', id: f.id, w: Math.max(40, f.w + d[0] * step), h: Math.max(40, (f.h ?? 200) + d[1] * step) });
          } else {
            st.dispatch({ t: 'frame.move', ids: sel, dx: d[0] * step, dy: d[1] * step });
          }
          return;
        }
        const frame = st.doc.frames.find((f) => f.id === ls.frameId);
        if (!frame) return;
        const html = getRenderHtml(frame, framesById(st.doc.frames));
        const next =
          e.key === 'ArrowUp' ? parentCrumb(ancestorChain(html, ls.cfNodeId), ls.cfNodeId)
          : e.key === 'ArrowDown' ? firstChildLayer(html, ls.cfNodeId)
          : e.key === 'ArrowLeft' ? siblingLayer(html, ls.cfNodeId, 'prev')
          : siblingLayer(html, ls.cfNodeId, 'next');
        if (next) {
          e.preventDefault();
          st.setLayerSelection({ frameId: ls.frameId, cfNodeId: next });
        }
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'v' || e.key === 'V')) {
        e.preventDefault();
        void pasteAsEmbed();
      }
      // ⌘O → Projects screen.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'o' || e.key === 'O')) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        useProjectsScreen.getState().openScreen();
        return;
      }
      // ⇧⌘N → new TSX component. (Creation → blocked for viewers.)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && (e.key === 'n' || e.key === 'N')) {
        if (isEditableTarget(e.target) || currentReadOnly()) return;
        e.preventDefault();
        spawnTsxComponent();
        return;
      }
      // ⇧⌘K → new conversation in the editor panel chat.
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && (e.key === 'k' || e.key === 'K')) {
        if (isEditableTarget(e.target) || currentReadOnly()) return;
        e.preventDefault();
        const st = useCanvasStore.getState();
        const id = ensurePanelChat({ frames: st.doc.frames, dispatch: st.dispatch });
        newConversation(id);
        usePanelStore.getState().setOpen(true);
        return;
      }
      // ⌘E / Ctrl+E → toggle the docked Editor Panel.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'e' || e.key === 'E')) {
        e.preventDefault();
        usePanelStore.getState().toggle();
      }
      // ⌘T / Ctrl+T → toggle the motion timeline dock.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 't' || e.key === 'T')) {
        e.preventDefault();
        useTimelineStore.getState().toggle();
        return;
      }
      // ⌘Z undo · ⌘⇧Z / ⌘Y redo. Skipped while typing so text fields keep their
      // own undo (CodeMirror, textareas, contenteditable slots).
      if ((e.metaKey || e.ctrlKey) && !e.altKey && (e.key === 'z' || e.key === 'Z')) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        if (e.shiftKey) useCanvasStore.getState().redo();
        else useCanvasStore.getState().undo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'y' || e.key === 'Y')) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        useCanvasStore.getState().redo();
        return;
      }
      // ⌘A → select all frames. ⌘D → duplicate selection. Skipped while typing.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'a' || e.key === 'A')) {
        if (isEditableTarget(e.target)) return;
        e.preventDefault();
        selectAll();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && (e.key === 'd' || e.key === 'D')) {
        if (isEditableTarget(e.target) || currentReadOnly()) return;
        // A drilled-in layer duplicates the sub-element subtree, not the frame.
        const ls = useCanvasStore.getState().layerSelection;
        if (ls) {
          e.preventDefault();
          useCanvasStore.getState().dispatch({ t: 'frame.duplicateNode', frameId: ls.frameId, cfNodeId: ls.cfNodeId });
          return;
        }
        if (useCanvasStore.getState().selection.length === 0) return;
        e.preventDefault();
        duplicateSelection();
        return;
      }
      // V / H → Select / Hand tool (no modifier). Skipped while typing.
      if (!e.metaKey && !e.ctrlKey && !e.altKey && (e.key === 'v' || e.key === 'V' || e.key === 'h' || e.key === 'H')) {
        if (isEditableTarget(e.target)) return;
        useToolStore.getState().setTool(e.key.toLowerCase() === 'v' ? 'select' : 'hand');
        return;
      }
      // Delete / Backspace → remove the selection (one undo). Skipped while typing.
      if ((e.key === 'Delete' || e.key === 'Backspace') && !e.metaKey && !e.ctrlKey) {
        if (isEditableTarget(e.target) || currentReadOnly()) return;
        // A drilled-in layer deletes the sub-element subtree, then steps out to
        // the frame (the cf-id is gone). Frame-level delete is the fallback.
        const ls = useCanvasStore.getState().layerSelection;
        if (ls) {
          e.preventDefault();
          useCanvasStore.getState().dispatch({ t: 'frame.deleteNode', frameId: ls.frameId, cfNodeId: ls.cfNodeId });
          useCanvasStore.getState().setLayerSelection(null);
          return;
        }
        if (useCanvasStore.getState().selection.length === 0) return;
        e.preventDefault();
        deleteSelection();
        return;
      }
      // Figma-style viewport keys. Use e.code (not e.key) because Shift turns
      // the digit row into '!'/'@'/')'. Shift+1 fit all · Shift+2 fit selection
      // · Shift+0 reset to 100%. Skipped while typing.
      if (e.shiftKey && !e.metaKey && !e.ctrlKey && !e.altKey &&
          (e.code === 'Digit1' || e.code === 'Digit2' || e.code === 'Digit0')) {
        if (isEditableTarget(e.target)) return;
        const st = useCanvasStore.getState();
        if (e.code === 'Digit0') {
          // Reset zoom to 100%, keeping the current view center fixed.
          e.preventDefault();
          const { pan, zoom } = st.doc.viewport;
          const cx = (window.innerWidth / 2 - pan.x) / zoom;
          const cy = (window.innerHeight / 2 - pan.y) / zoom;
          st.dispatch({ t: 'viewport.set', pan: { x: window.innerWidth / 2 - cx, y: window.innerHeight / 2 - cy }, zoom: 1 });
          return;
        }
        const visible = st.doc.frames.filter((f) => !isPanelOwned(f) && f.kind !== 'note');
        const target =
          e.code === 'Digit2' ? visible.filter((f) => st.selection.includes(f.id)) : visible;
        const b = boundsOf(target.length ? target : visible);
        if (b) {
          e.preventDefault();
          st.dispatch({ t: 'viewport.focus', bounds: { ...b, w: Math.max(b.w, 1), h: Math.max(b.h, 1) } });
        }
      }
    };
    // Paste/drop create frames — block them for viewers (server would reject).
    const onPaste = (e: ClipboardEvent) => { if (!currentReadOnly()) handlePaste(e); };
    const onDrop = (e: DragEvent) => { if (currentReadOnly()) return; handleDrop(e); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('paste', onPaste);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', onDrop);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('paste', onPaste);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // URL is the source of truth: react to the route's project id. `/p/<id>` opens
  // that project (reload-safe); `/` (no id, e.g. after Back) shows the projects
  // screen. Runs on mount + whenever the route param changes.
  useEffect(() => {
    const screen = useProjectsScreen.getState();
    if (routeProjectId) {
      if (useCurrentProject.getState().projectId !== routeProjectId) {
        void openProjectById(routeProjectId).then(() => screen.close()).catch(() => screen.openScreen());
      } else {
        // Already loaded (e.g. Forward back onto this project) — make sure the
        // projects overlay isn't left covering the canvas.
        screen.close();
      }
    } else {
      screen.openScreen();
    }
  }, [routeProjectId]);

  // ⌘O / Ctrl+O opens the projects screen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'o') { e.preventDefault(); useProjectsScreen.getState().openScreen(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Push debounced doc changes to the server while a project is open. Skips the
  // synthetic store change from a programmatic doc load (project open / adopt)
  // so opening a project doesn't immediately re-push + bump the server version.
  // When live sync is on, the Hocuspocus provider owns persistence — this REST
  // autosave would PUT a stale baseVersion and 409 against the docVersion the
  // sync layer advances, so it's disabled in that mode.
  // First-run replay: once per client, only into an empty canvas (new users /
  // fresh projects), never for viewers. Esc/click fast-forwards.
  useEffect(() => {
    if (hasReplayed()) return;
    const t = window.setTimeout(() => {
      const st = useCanvasStore.getState();
      if (st.doc.frames.length === 0 && !currentReadOnly()) void runFirstRunReplay();
    }, 1500);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!currentProjectId || isLiveSyncEnabled()) return;
    let t: number | undefined;
    const unsub = useCanvasStore.subscribe((s) => {
      if (isApplyingDoc()) return;
      if (t) clearTimeout(t);
      const doc = s.doc;
      t = window.setTimeout(() => { void syncController.saveCurrent(currentProjectId, encodeDoc(doc)); }, 1500);
    });
    return () => { if (t) clearTimeout(t); unsub(); };
  }, [currentProjectId]);

  return (
    <PresenceProvider>
    <div className="h-screen w-screen relative">
      {/* No canvas-wide data-theme: the active theme is applied per DESIGN frame
          (render/applyTheme) so canvas-native tool frames (chat/code/fx/note/
          styleguide), which use the chrome --color-* vars, never inherit a
          theme's colliding color tokens (which broke dark mode). CanvasSurface
          owns the canvas, frame chrome, and the AnchorEdges/CommentPins/ChatChips
          overlay; on non-Canary browsers it self-falls-back to DOM positioning. */}
      <CanvasSurface />
      <Marquee />
      <LayerOverlay />
      <LayerDropIndicator />
      <FlashOverlay />
      <TimelinePanel />
      <CommentPopover />
      <MediaSwapModal />
      <StockPhotoModal />
      <CheckpointsModal />
      <PublishModal />
      <ConnectAgentHost />
      <ActionChip />
      <SizeInspectorSlot />
      <ProfileMenu />
      <ProjectNameChip />
      <SettingsModal />
      <RemoteSelection />
      <PresenceCursors />
      <Facepile />
      <ConnectionStatus />
      <SyncUpgradeBanner />
      <TourPlayer />
      <CmdK />
      <ContextMenu />
      <CommandPalette commands={commands} />
      <EditorPanel />
      {bookmarkletOpen && <BookmarkletModal onClose={() => setBookmarkletOpen(false)} />}
      <ProjectsScreen />
    </div>
    </PresenceProvider>
  );
}

/** Hosts the Connect-agent (MCP pairing) modal, gated by the panel-store flag
 *  the ⌘P "Connect agent…" command flips. Bridges the store flag to
 *  ConnectAgent's onClose prop (mirrors CheckpointsModal's flag-driven host). */
function ConnectAgentHost() {
  const open = usePanelStore((s) => s.connectAgentOpen);
  const setOpen = usePanelStore((s) => s.setConnectAgentOpen);
  if (!open) return null;
  return <ConnectAgent onClose={() => setOpen(false)} />;
}

/** True when the keyboard event originates from a text-entry surface. */
function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  return tag === 'input' || tag === 'textarea' || el.isContentEditable;
}

/** SizeInspector hides while the timeline dock is open (both live at the
 *  bottom edge; the dock owns that space). */
function SizeInspectorSlot() {
  const timelineOpen = useTimelineStore((s) => s.open);
  if (timelineOpen) return null;
  return <SizeInspector />;
}
